import { ReactNode } from 'react';

import { DialogDescription, DialogTitle } from '@superfunnel/ui-core/Dialog';
import { classNames } from '@superfunnel/ui-core/utils';

export interface PromptModalPoint {
  icon: ReactNode;
  title: string;
  description: string;
}

interface PromptModalBodyProps {
  leadingTile: ReactNode;
  trailingTile: ReactNode;
  title: ReactNode;
  description: ReactNode;
  points?: PromptModalPoint[];
}

function AppTile({ children }: { children: ReactNode }) {
  return (
    <div className="flex size-14 items-center justify-center rounded-2xl border border-base-200 bg-card shadow-sm">
      {children}
    </div>
  );
}

export function PromptModalBody({
  leadingTile,
  trailingTile,
  title,
  description,
  points = [],
}: PromptModalBodyProps) {
  return (
    <div className="flex flex-col gap-6">
      <div
        className={classNames(
          '-mx-4 -mt-4 flex flex-col items-center gap-5 rounded-t-2xl bg-gradient-to-b from-brand-500/15 via-brand-500/5 to-transparent px-6 pt-10 pb-7',
          points.length > 0 && 'border-b border-base-200',
        )}
      >
        <div className="flex items-center gap-3">
          <AppTile>{leadingTile}</AppTile>
          <div className="flex items-center gap-1.5">
            <span className="size-1.5 rounded-full bg-brand-500/30" />
            <span className="size-1.5 rounded-full bg-brand-500/50" />
            <span className="size-1.5 rounded-full bg-brand-500/30" />
          </div>
          <AppTile>{trailingTile}</AppTile>
        </div>

        <div className="flex flex-col items-center gap-2">
          <DialogTitle className="text-center text-lg/[1.3] tracking-[-0.01em] text-balance text-text-primary">
            {title}
          </DialogTitle>
          <DialogDescription className="max-w-sm text-center text-sm/[1.4] text-pretty text-base-500">
            {description}
          </DialogDescription>
        </div>
      </div>

      {points.length > 0 && (
        <ul className="flex flex-col gap-4 px-1">
          {points.map((point) => (
            <li key={point.title} className="flex gap-3">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-500/10 text-brand-500">
                {point.icon}
              </span>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-text-primary">
                  {point.title}
                </span>
                <span className="text-xs/[1.4] text-pretty text-base-500">
                  {point.description}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
